/**
 * Сценарии «что если» (ПЛ-20): сдвиг поставки, дополнительная бригада, другая длительность, календарь или лаг.
 * Сценарий считается без правки графика и сравнивается с текущим; «Применить» — одна правка журнала.
 */
import {
  SCENARIO_KINDS,
  type ScenarioChange,
  type ScenarioResult,
  type ScenarioRow,
  type ScheduleView,
  VARIANT_CRITERIA,
  type VariantReport,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button, IconButton } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from './LField';
import { fmtMoment } from './model';

type Kind = keyof typeof SCENARIO_KINDS;
const n = (x: number) => `${x > 0 ? '+' : ''}${String(x).replace('.', ',')}`;

function blank(kind: Kind, view: ScheduleView): ScenarioChange {
  const a =
    (
      view.activities.find((x) => x.type === 'task' && !x.actualStart) ??
      view.activities.find((x) => x.type === 'task')
    )?.id ??
    view.activities[0]?.id ??
    '';
  if (kind === 'lag') return { kind, relationId: view.relations[0]?.id ?? '', days: 0 };
  if (kind === 'calendar') return { kind, activityId: null, calendarId: view.calendars[0]?.id ?? '' };
  if (kind === 'crews') return { kind, activityId: a, add: 1 };
  // Остаток задаётся начатой работе
  if (kind === 'remaining')
    return {
      kind,
      activityId: view.activities.find((x) => x.type === 'task' && x.actualStart && !x.actualFinish)?.id ?? a,
      days: 5,
    };
  return { kind, activityId: a, days: kind === 'delay' ? 7 : 5 };
}

export function ScenariosPanel({ view, onView }: { view: ScheduleView; onView: (v: ScheduleView) => void }) {
  const id = view.schedule.id;
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['scenarios', id],
    queryFn: () => api<ScenarioRow[]>(`/schedules/${id}/scenarios`),
  });
  const [sel, setSel] = useState<string | null>(null);
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api<ScenarioRow>(`/schedules/${id}/scenarios`, { body: { name, changes: [] } }),
    onSuccess: (r) => {
      setName('');
      setSel(r.id);
      void qc.invalidateQueries({ queryKey: ['scenarios', id] });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const forecast = useMutation({
    mutationFn: () => api<ScenarioRow>(`/schedules/${id}/scenarios/forecast`, { method: 'POST' }),
    onSuccess: (r) => {
      setSel(r.id);
      toast('Сценарий прогноза по выработке создан');
      void qc.invalidateQueries({ queryKey: ['scenarios', id] });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const current = list.data?.find((x) => x.id === sel) ?? null;
  return (
    <div className="flex flex-col gap-4">
      <Panel title="Сценарии «что если»" meta={list.data ? `${list.data.length} из 30` : undefined}>
        <p className="mb-3 text-[13px] text-ink-2">
          Проверьте, что будет со сроками, если поставка задержится, добавится бригада или изменится
          календарь. Сценарий считается без правки графика; подходящий можно применить.
        </p>
        {view.can.edit ? (
          <form
            className="mb-3 flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <LField label="Название сценария">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например, «Насосы позже на месяц»"
                className="w-72 max-w-full max-md:h-11"
              />
            </LField>
            <Button type="submit" loading={create.isPending} disabled={!name.trim()} className="max-md:h-11">
              + Новый сценарий
            </Button>
            <Button
              type="button"
              loading={forecast.isPending}
              onClick={() => forecast.mutate()}
              className="max-md:h-11"
            >
              Прогноз по выработке
            </Button>
          </form>
        ) : null}
        {list.isPending ? <Spinner /> : null}
        {list.isError ? <ErrorBox error={list.error} /> : null}
        {list.data && !list.data.length ? (
          <EmptyState title="Сценариев пока нет">
            {view.can.edit ? 'Создайте первый сценарий.' : 'Сценарии создаёт тот, кто правит график.'}
          </EmptyState>
        ) : null}
        <ul className="divide-y divide-line text-[14px]">
          {(list.data ?? []).map((x) => (
            <li key={x.id}>
              <button
                type="button"
                onClick={() => setSel(x.id === sel ? null : x.id)}
                className={clsx(
                  'flex min-h-11 w-full items-center justify-between gap-2 px-1 text-left hover:bg-surface-2',
                  x.id === sel && 'font-semibold',
                )}
              >
                <span className="min-w-0 truncate">{x.name}</span>
                <span className="flex shrink-0 items-center gap-2 text-[12px] text-ink-2">
                  изменений {x.changes.length}
                  {x.appliedAt ? <Badge tone="success">применён</Badge> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Panel>
      {list.data?.length ? <VariantsBlock view={view} /> : null}
      {current ? (
        <ScenarioEditor
          key={current.id}
          view={view}
          sc={current}
          onDeleted={() => setSel(null)}
          onApplied={async () => {
            onView(await api<ScheduleView>(`/schedules/${id}`));
          }}
        />
      ) : null}
    </div>
  );
}

function ScenarioEditor({
  view,
  sc,
  onDeleted,
  onApplied,
}: {
  view: ScheduleView;
  sc: ScenarioRow;
  onDeleted: () => void;
  onApplied: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(sc.name);
  const [changes, setChanges] = useState<ScenarioChange[]>(sc.changes);
  const dirty = name !== sc.name || JSON.stringify(changes) !== JSON.stringify(sc.changes);
  useEffect(() => {
    setName(sc.name);
    setChanges(sc.changes);
  }, [sc]);
  const res = useQuery({
    queryKey: ['scenario-result', sc.id, view.schedule.revision, JSON.stringify(sc.changes)],
    queryFn: () => api<ScenarioResult>(`/scenarios/${sc.id}/result`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['scenarios', view.schedule.id] });
  const save = useMutation({
    mutationFn: () => api<ScenarioRow>(`/scenarios/${sc.id}`, { method: 'PATCH', body: { name, changes } }),
    onSuccess: () => {
      toast('Сценарий сохранён, расчёт обновлён');
      void refresh();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const del = useMutation({
    mutationFn: () => api(`/scenarios/${sc.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      onDeleted();
      void refresh();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const apply = useMutation({
    mutationFn: () => api<{ changed: number }>(`/scenarios/${sc.id}/apply`, { method: 'POST' }),
    onSuccess: async (r) => {
      toast(`Сценарий применён: изменено ${r.changed}. Отменить можно в журнале правок`, 'success');
      void refresh();
      await onApplied();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const edit = view.can.edit;
  const acts = [...view.activities].sort((a, b) => a.code.localeCompare(b.code, 'ru', { numeric: true }));
  const code = new Map(view.activities.map((a) => [a.id, a.code]));
  const set = (i: number, c: ScenarioChange) => setChanges(changes.map((x, j) => (j === i ? c : x)));
  const actSelect = (i: number, c: ScenarioChange & { activityId: string | null }, allowAll = false) => (
    <Select
      value={c.activityId ?? ''}
      disabled={!edit}
      onChange={(e) => set(i, { ...c, activityId: e.target.value || null } as ScenarioChange)}
      aria-label="Работа"
      className="w-64 max-w-full max-md:h-11"
    >
      {allowAll ? <option value="">Все работы на основном календаре</option> : null}
      {acts.map((a) => (
        <option key={a.id} value={a.id}>
          {a.code} {a.name}
        </option>
      ))}
    </Select>
  );
  const r = res.data;
  return (
    <>
      <Panel
        title={sc.name}
        meta={sc.appliedAt ? `применён ${fmtMoment(sc.appliedAt.slice(0, 16))}` : undefined}
        actions={
          edit ? (
            <IconButton
              label="Удалить сценарий"
              onClick={() => window.confirm(`Удалить сценарий «${sc.name}»?`) && del.mutate()}
              className="max-md:size-11"
            >
              <Trash2 size={16} />
            </IconButton>
          ) : null
        }
      >
        {edit ? (
          <LField label="Название">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-72 max-w-full max-md:h-11"
            />
          </LField>
        ) : null}
        <ul className="my-3 flex flex-col gap-2">
          {changes.map((c, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: изменения сценария без своего id, порядок задаёт человек
            <li key={i} className="flex flex-wrap items-end gap-2 rounded-md border border-line px-2 py-2">
              <LField label="Что меняем">
                <Select
                  value={c.kind}
                  disabled={!edit}
                  onChange={(e) => set(i, blank(e.target.value as Kind, view))}
                  className="max-md:h-11"
                >
                  {Object.entries(SCENARIO_KINDS).map(([k, t]) => (
                    <option key={k} value={k}>
                      {t}
                    </option>
                  ))}
                </Select>
              </LField>
              {c.kind === 'lag' ? (
                <LField label="Связь">
                  <Select
                    value={c.relationId}
                    disabled={!edit}
                    onChange={(e) => set(i, { ...c, relationId: e.target.value })}
                    className="w-64 max-w-full max-md:h-11"
                  >
                    {view.relations.map((x) => (
                      <option key={x.id} value={x.id}>
                        {code.get(x.predId)} → {code.get(x.succId)} ({x.type})
                      </option>
                    ))}
                  </Select>
                </LField>
              ) : (
                <LField label="Работа">{actSelect(i, c, c.kind === 'calendar')}</LField>
              )}
              {c.kind === 'calendar' ? (
                <LField label="Календарь">
                  <Select
                    value={c.calendarId}
                    disabled={!edit}
                    onChange={(e) => set(i, { ...c, calendarId: e.target.value })}
                    className="max-md:h-11"
                  >
                    {view.calendars.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </Select>
                </LField>
              ) : (
                <LField label={c.kind === 'crews' ? 'Добавить бригад' : 'Дней'}>
                  <NumText
                    disabled={!edit}
                    value={c.kind === 'crews' ? c.add : c.days}
                    onValue={(v) =>
                      set(
                        i,
                        c.kind === 'crews'
                          ? { ...c, add: Math.min(50, Math.max(1, Math.round(v))) }
                          : ({ ...c, days: c.kind === 'delay' ? Math.round(v) : v } as ScenarioChange),
                      )
                    }
                  />
                </LField>
              )}
              {edit ? (
                <IconButton
                  label="Убрать изменение"
                  onClick={() => setChanges(changes.filter((_, j) => j !== i))}
                  className="max-md:size-11"
                >
                  <Trash2 size={16} />
                </IconButton>
              ) : null}
            </li>
          ))}
        </ul>
        {edit ? (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setChanges([...changes, blank('delay', view)])}
              disabled={changes.length >= 500}
              className="max-md:h-11"
            >
              Добавить изменение
            </Button>
            <Button
              variant="primary"
              disabled={!dirty || !name.trim()}
              loading={save.isPending}
              onClick={() => save.mutate()}
              className="max-md:h-11"
            >
              Сохранить и посчитать
            </Button>
            <Button
              disabled={
                dirty || !sc.changes.length || Boolean(sc.appliedAt) || view.schedule.status === 'approved'
              }
              loading={apply.isPending}
              onClick={() =>
                window.confirm(
                  `Применить сценарий «${sc.name}» к графику? Изменения попадут в график одной правкой, её можно отменить в журнале.`,
                ) && apply.mutate()
              }
              className="max-md:h-11"
            >
              Применить к графику
            </Button>
          </div>
        ) : null}
        {dirty ? <p className="mt-2 text-[12px] text-ink-2">Сохраните, чтобы пересчитать сценарий.</p> : null}
      </Panel>
      <Panel title="Сценарий против текущего графика">
        {res.isPending ? <Spinner /> : null}
        {res.isError ? <ErrorBox error={res.error} /> : null}
        {r ? (
          <div className="flex flex-col gap-3 text-[14px]">
            <p>
              Окончание: {fmtMoment(r.finishCurrent)} → {fmtMoment(r.finish)}
              <b
                className={clsx(
                  'ml-2',
                  r.shiftDays > 0 ? 'text-danger' : r.shiftDays < 0 ? 'text-success' : '',
                )}
              >
                {n(r.shiftDays)} раб. дн
              </b>
              {r.mustFinishBy ? (
                <span className={clsx('ml-3', r.finish > r.mustFinishBy ? 'text-danger' : 'text-ink-2')}>
                  директивный срок {fmtMoment(r.mustFinishBy)}
                </span>
              ) : null}
              <span className="ml-3 text-ink-2">
                критических работ {r.criticalCurrent} → {r.critical}
              </span>
            </p>
            {r.skipped ? (
              <p className="text-[13px] text-danger">
                Не учтено изменений: {r.skipped} — работа удалена, уже начата или связи нет.
              </p>
            ) : null}
            {r.milestones.length ? (
              <div>
                <h3 className="mb-1 text-[13px] font-semibold">Вехи</h3>
                <ul className="divide-y divide-line">
                  {r.milestones.map((m) => (
                    <li key={m.id} className="flex flex-wrap justify-between gap-2 py-1.5">
                      <span>
                        {m.code} {m.name}
                      </span>
                      <span className={clsx('num', m.scenario > m.current ? 'text-danger' : 'text-ink-2')}>
                        {fmtMoment(m.current)} → {fmtMoment(m.scenario)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div>
              <h3 className="mb-1 text-[13px] font-semibold">Сдвинутые работы: {r.moved.length}</h3>
              <ul className="divide-y divide-line">
                {r.moved.slice(0, 50).map((m) => (
                  <li key={m.id} className="flex flex-wrap justify-between gap-2 py-1.5">
                    <span className="min-w-0">
                      {m.code} {m.name}
                    </span>
                    <span className="num text-ink-2">
                      окончание {fmtMoment(m.finishCurrent)} → {fmtMoment(m.finish)}{' '}
                      <span className={m.shiftDays > 0 ? 'text-danger' : 'text-success'}>
                        ({n(m.shiftDays)})
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
      </Panel>
    </>
  );
}

/** Число в поле: текст хранится как набран («-», «1,»), значение уходит, когда текст — число */
function NumText({
  value,
  onValue,
  disabled,
}: {
  value: number;
  onValue: (v: number) => void;
  disabled?: boolean;
}) {
  const [text, setText] = useState(String(value).replace('.', ','));
  useEffect(() => {
    setText((t) => (Number(t.replace(',', '.')) === value ? t : String(value).replace('.', ',')));
  }, [value]);
  return (
    <Input
      inputMode="decimal"
      disabled={disabled}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const v = Number(e.target.value.replace(',', '.'));
        if (e.target.value.trim() !== '' && Number.isFinite(v)) onValue(v);
      }}
      onBlur={() => setText(String(value).replace('.', ','))}
      className="w-24 max-md:h-11"
    />
  );
}

/** Подбор варианта графика (ПЛ-41): текущий график и сценарии по выбранному критерию */
function VariantsBlock({ view }: { view: ScheduleView }) {
  const [criterion, setCriterion] = useState<keyof typeof VARIANT_CRITERIA>('finish');
  const q = useQuery({
    queryKey: ['variants', view.schedule.id, criterion, view.schedule.revision],
    queryFn: () => api<VariantReport>(`/schedules/${view.schedule.id}/variants`, { query: { criterion } }),
  });
  return (
    <Panel title="Подбор варианта" meta="текущий график и сценарии">
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <LField label="Критерий">
          <Select
            value={criterion}
            onChange={(e) => setCriterion(e.target.value as keyof typeof VARIANT_CRITERIA)}
            className="max-md:h-11"
          >
            {Object.entries(VARIANT_CRITERIA).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </Select>
        </LField>
        {q.data?.mustFinishBy ? (
          <span className="text-[13px] text-ink-2">
            Допустимые — не позже директивного срока {fmtMoment(q.data.mustFinishBy)}
          </span>
        ) : null}
      </div>
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {q.data ? (
        <ul className="divide-y divide-line text-[14px]">
          {q.data.rows.map((r) => (
            <li
              key={r.scenarioId ?? 'current'}
              className="flex flex-wrap items-center justify-between gap-2 py-1.5"
            >
              <span className={clsx('min-w-0', r.rank === 1 && 'font-semibold')}>
                {r.rank}. {r.name}
                {!r.feasible ? (
                  <Badge tone="danger" className="ml-2">
                    позже срока
                  </Badge>
                ) : r.rank === 1 ? (
                  <Badge tone="success" className="ml-2">
                    лучший
                  </Badge>
                ) : null}
              </span>
              <span className="num text-[12px] text-ink-2">
                окончание {fmtMoment(r.finish)}
                {r.shiftDays ? ` (${n(r.shiftDays)} раб. дн)` : ''} · пик {String(r.peak).replace('.', ',')} ·
                неровность {String(r.unevenness).replace('.', ',')} % · критических {r.critical}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="mt-2 text-[12px] text-ink-2">
        Пик — наибольшее число людей и машин в день; неровность — насколько загрузка по дням отклоняется от
        средней. Выбранный вариант применяется из карточки сценария.
      </p>
    </Panel>
  );
}
